import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Alert } from "@models/alerts/alert";
import { AlertRecord } from '@models/alerts/alert-record';
import { AlertStatus, AlertType } from "@models/alerts/alerts-enums";
import { FileData } from '@models/ui-models/files';
import { NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle } from "@ng-bootstrap/ng-bootstrap";
import { postponeMenu } from "@pages/alerts/constants/postpone-options";
import { AlertHelper } from "@pages/alerts/utils/alert-helper";
import { getPostponeDate } from "@pages/alerts/utils/get-postpone-date";
import {
  AgActionIconButtonComponent
} from "@shared/components/grid/ag-cells/common/ag-action-icon-button/ag-action-icon-button.component";
import { HasRoleDirective } from '@shared/directives/has-role.directive';
import { AlertsService } from "@shared/services/alert.service";
import { AlertsApiService } from "@shared/services/alerts-api.service";
import { downloadBase64File } from '@shared/utils/download-base64-file';
import { ICellRendererAngularComp } from "ag-grid-angular";
import { ICellRendererParams } from "ag-grid-community";
import { observe, Observer } from "fast-json-patch";
import { ToastrService } from 'ngx-toastr';
import { of, switchMap, tap } from "rxjs";

type ActionEvent = (data: unknown) => void;

type CellRendererParams = ICellRendererParams<AlertRecord> & {
  openAlert: ActionEvent,
  markRead: ActionEvent,
  markRequireAttention: ActionEvent,
  archive: ActionEvent,
  ignore: ActionEvent,
  postpone: ActionEvent,
  download: ActionEvent,
};

@Component({
  selector: 'app-ag-alerts-actions',
  standalone: true,
  imports: [
    NgbDropdown,
    NgbDropdownItem,
    NgbDropdownMenu,
    NgbDropdownToggle,
    HasRoleDirective,
    AgActionIconButtonComponent
  ],
  templateUrl: './ag-alerts-actions.component.html',
  styleUrl: './ag-alerts-actions.component.scss'
})
export class AgAlertsActionsComponent implements ICellRendererAngularComp {
  protected alertService = inject(AlertsService);
  protected alertsApiService = inject(AlertsApiService);
  protected destroyRef = inject(DestroyRef);
  protected toastr = inject(ToastrService);

  protected readonly AlertType = AlertType;
  protected readonly AlertStatus = AlertStatus;

  observer!: Observer<Alert>;
  alertRecord: AlertRecord | undefined;

  showDownloadBtn = false;
  alertTypeListForDownload = [
    AlertType.ValidationReport,
    AlertType.DebtReports,
    AlertType.AsicInvoice,
    AlertType.ChangeOfNameCertificate,
    AlertType.CompanyStatement,
    AlertType.Others
  ];

  //   Only alerts with the following types can be ignored or postponed:
  // - XPM Sync Issues
  // - Identified External Changes
  // - ASIC Data Issues
  // - ASIC Connection Issues -- need to add later
  // - Failed Emails -- need to add later
  alertTypesForIgnoredOrPostponed = [
    AlertType.XpmSyncIssue,
    AlertType.IdentifiedExternalChanges,
    AlertType.AsicDataIssue
  ];
  allowChangeToIgnoredOrPostponed = false;
  type: AlertType | undefined;
  status: AlertStatus | undefined;
  isRead: boolean | undefined;
  needAttention: boolean | undefined;
  canBeArchived = false;

  postponeMenu = postponeMenu;

  protected params!: CellRendererParams;

  agInit(params: CellRendererParams): void {
    this.params = params;
    if (params.data !== undefined) {
      this.type = params.data.type;
      this.allowChangeToIgnoredOrPostponed = AlertHelper.isPostponeVisible(this.params.data);
      this.status = params.data.status;
      this.isRead = params.data.isRead;
      this.needAttention = params.data.needAttention;
      this.alertRecord = params.data;
      this.observer = observe(this.alertRecord);
    }

    if (this.type !== undefined) {
      this.showDownloadBtn = this.alertTypeListForDownload.includes(this.type);
      this.canBeArchived = AlertHelper.isArchiveVisible(this.params.data);
    }
  }

  refresh(params: CellRendererParams): boolean {
    this.params = params;
    return true;
  }

  openAlert(): void {
    const data = { alertRecord: this.params.data, index: this.params.node.rowIndex };
    this.params.openAlert(data);
  }

  markRead(): void {
    if (this.params.data) {
      this.params.data.isRead = true;
      const alertId = this.params.data.id;

      this.alertsApiService.updateAlert(alertId, this.observer).pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(alert => {
        if (alert.id) {
          this.updateSingleRow(alert);
          this.alertService.notifyReadStatusUpdated();
          this.toastr.success('Alert(s) marked as read', 'Success');
        }
      });
    }
  }

  markRequireAttention(): void {
    if (this.params.data) {
      this.params.data.needAttention = true;
      const alertId = this.params.data.id;

      this.alertsApiService.updateAlert(alertId, this.observer).pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(alert => {
        if (alert.id) {
          this.updateSingleRow(alert);
        }
      });
    }
  }

  archive(): void {
    this.updateAlertStatus(AlertStatus.Archived);
  }

  ignore(): void {
    this.updateAlertStatus(AlertStatus.Ignored);
  }

  postpone(numberOfDays: number): void {
    if (this.params.data) {
      this.params.data.postponedTill = getPostponeDate(numberOfDays);
    }
    this.updateAlertStatus(AlertStatus.Postponed);
  }

  private updateAlertStatus(status: AlertStatus): void {
    if (this.params.data) {
      this.params.data.status = status;
      this.alertRecord!.status = status;
      const alertId = this.params.data.id;

      this.alertsApiService.updateAlert(alertId, this.observer).pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(alert => {
        if (alert.id) {
          this.updateSingleRow(alert);
          this.toastr.success(`Selected alert(s) ${ AlertStatus[status] }`, 'Success');
        }
      });
    }
  }

  download(): void {
    if (this.params.data) {
      const alertId = this.params.data.id;

      this.alertsApiService.downloadAllAttachments(alertId).pipe(
        switchMap((res: FileData) => {
          if (res && this.params.data) {
            downloadBase64File(res.fileByte, res.fileName);
            this.toastr.success("Attachments have been successfully downloaded", "Success");
            this.params.data.isRead = true;
            const alertId = this.params.data.id;
            return this.alertsApiService.updateAlert(alertId, this.observer).pipe(
              tap(alert => {
                if (alert.id) {
                  this.updateSingleRow(alert);
                }
              })
            );
          }

          return of('');
        }),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe();
    }
  }

  private updateSingleRow(data: Alert) {
    const rowNodeId = this.params.node.id;

    if (rowNodeId) {
      this.params.node.setData(Alert.toAlertRecord(data));
      this.params.api.refreshServerSide({ route: [rowNodeId] });
      this.params.api.refreshCells({ rowNodes: [this.params.node] });
    }
  }
}
